import type { Order, OrderItem, OrderStatus } from '../types';
import { getAmountPaid, getOrderTotals, hasBalanceDue, isOrderItemized } from './orderUtils';

/**
 * Non-blocking order-level statuses for register / EOD close.
 * `comped` is terminal for the guest bill; `completed` covers legacy paths.
 */
const TERMINAL_ORDER_STATUSES = new Set<OrderStatus>([
  'served',
  'completed',
  'cancelled',
  'refunded',
  'comped',
]);

function everyServableLineFullyDelivered(order: Order): boolean {
  const items = (order.items ?? []).filter((i) => !i.removed && (i.quantity ?? 0) > 0);
  if (items.length === 0) return true;
  return items.every((item) => {
    const qty = item.quantity ?? 0;
    if (item.price != null && item.price < 0) return true;
    if (String(item.id).includes('combo-discount')) return true;
    const served = item.quantityServed ?? (item.served === true ? qty : 0);
    return served >= qty;
  });
}

/**
 * True when this order should block register close / EOD checklist:
 * paid, not in a terminal order status, and not all lines fully served to the customer.
 *
 * Fixes false positives where `order.status` is still `ready`/`preparing` but every line
 * already has quantityServed (Service screen / KDS) while the header status was not updated.
 */
export function isPaidOrderBlockingRegisterClose(o: Order): boolean {
  if (o.paymentStatus !== 'paid') return false;
  if (TERMINAL_ORDER_STATUSES.has(o.status)) return false;
  if (everyServableLineFullyDelivered(o)) return false;
  return true;
}

export function getPaidOrdersBlockingRegisterClose(orders: Order[]): Order[] {
  return orders.filter(isPaidOrderBlockingRegisterClose);
}

/**
 * True when the order is settled for payment (register close / EOD).
 * Aligns with POS badges: full payments recorded, comped, or paymentStatus paid with no balance.
 */
export function isOrderEffectivelyPaid(order: Order): boolean {
  if (order.isDeleted) return true;
  if (order.status === 'cancelled' || order.status === 'refunded') return true;
  if (order.isComped || order.status === 'comped') return true;

  const { totalAmount } = getOrderTotals(order);
  const paid = getAmountPaid(order);

  if (order.paymentStatus === 'paid') {
    if (!isOrderItemized(order)) return true;
    if (!hasBalanceDue(order)) return true;
  }

  if (order.paymentStatus === 'unpaid') {
    if (totalAmount > 0 && paid >= totalAmount - 0.01 && !hasBalanceDue(order)) return true;
    if (totalAmount <= 0.01 && !hasBalanceDue(order) && paid <= 0.01) {
      return order.status === 'served' || order.status === 'completed';
    }
  }

  return false;
}

/** Orders that block register close because payment is not fully settled. */
export function isOrderUnsettledForRegisterClose(order: Order): boolean {
  if (order.status === 'cancelled' || order.status === 'refunded' || order.isDeleted) return false;
  return !isOrderEffectivelyPaid(order);
}

export function getUnsettledOrdersForRegisterClose(orders: Order[]): Order[] {
  return orders.filter(isOrderUnsettledForRegisterClose);
}

/** Unpaid in Firestore but payments already cover the total — safe to sync paymentStatus to paid. */
export function isOrderStuckUnpaidWithFullPayment(order: Order): boolean {
  if (order.paymentStatus !== 'unpaid') return false;
  if (order.isDeleted || order.status === 'cancelled' || order.status === 'refunded') return false;
  if (order.isComped || order.status === 'comped') return false;
  const payments = Array.isArray(order.payments) ? order.payments : [];
  if (payments.length === 0) return false;
  const settled = payments.filter((p) => p.paymentStatus !== 'pending');
  if (settled.length === 0) return false;
  const { totalAmount } = getOrderTotals(order);
  const paid = getAmountPaid(order);
  return totalAmount > 0 && paid >= totalAmount - 0.01 && !hasBalanceDue(order);
}

export function getOrdersStuckUnpaidWithFullPayment(orders: Order[]): Order[] {
  return orders.filter(isOrderStuckUnpaidWithFullPayment);
}

/**
 * Force order + lines to a consistent "served" snapshot for end-of-day register close.
 * Use only after manager confirmation (e.g. food already went out but status was never updated).
 */
export function applyRegisterCloseServedState(order: Order): Order {
  const now = Date.now();
  const items: OrderItem[] = (order.items ?? []).map((item) => {
    if (item.removed) return item;
    const qty = item.quantity ?? 0;
    if (qty <= 0) return item;
    return {
      ...item,
      status: 'completed',
      quantityCompleted: item.quantityCompleted ?? qty,
      quantityServed: qty,
      served: true,
    };
  });
  return {
    ...order,
    status: 'served',
    servedAt: order.servedAt ?? now,
    items,
  };
}
